import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { userService, tenantService } from '../services/api';
import { EditProfileModal, DeleteConfirmModal } from '../components/UserModals';
import { TenantAssignmentModal, TenantBadge, ProductChip, MODULE_LABELS, enrolledModulesFromProducts } from '../components/TenantComponents';

/**
 * Reject an access request.
 *
 * Its own modal rather than a window.confirm because the note is OPTIONAL but
 * consequential: whatever is typed here is emailed verbatim to the person being
 * turned away. The field is labelled for that, not as an internal memo — the
 * failure mode is an admin writing "spam, ignore" and it landing in a real
 * customer's inbox.
 *
 * Rejection is reversible (the row keeps an "Approve access" action afterwards),
 * so this warns rather than blocks — but it does say the email is sent, because
 * the send is immediate and cannot be recalled.
 */
function RejectAccessModal({ user, loading, onClose, onConfirm }) {
  const [note, setNote] = useState('');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Reject access request</h2>
        <p>
          <strong>{user.email || user.username}</strong> will not be able to sign in to any
          GaliGali application, and any session they currently hold ends immediately.
        </p>

        <div className="form-group">
          <label htmlFor="reject-note">
            Reason <span style={{ fontWeight: 400, color: '#666' }}>(optional — sent to them)</span>
          </label>
          <textarea
            id="reject-note"
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. We could not match this address to a partner account."
          />
          <small style={{ color: '#666' }}>
            This text is included in the rejection email exactly as written. Leave it blank to
            send the standard message.
          </small>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={() => onConfirm(note)} disabled={loading}>
            {loading ? 'Rejecting…' : 'Reject and send email'}
          </button>
        </div>
      </div>
    </div>
  );
}

const Users = () => {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [userTenants, setUserTenants] = useState({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  // `approval` is seeded from the query string so the "someone is waiting"
  // email can deep-link straight to the review queue (/users?approval=pending).
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    tenantId: searchParams.get('tenantId') || 'all',
    migrationStatus: 'all',
    approval: searchParams.get('approval') || 'all',
  });
  const [rejectUser, setRejectUser] = useState(null);
  const [sorting, setSorting] = useState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const [summary, setSummary] = useState({ total: 0, migrated: 0, pending: 0 });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editModalUser, setEditModalUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [tenantAssignUser, setTenantAssignUser] = useState(null);
  const [bulkMigrateLoading, setBulkMigrateLoading] = useState(false);

  // Fetch tenants for filter dropdown
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await tenantService.getAll();
        setTenants(response.data || []);
      } catch (err) {
        console.error('Error fetching tenants:', err);
      }
    };
    fetchTenants();
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        status: filters.status,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      };
      if (filters.migrationStatus !== 'all') {
        params.migrationStatus = filters.migrationStatus;
      }
      if (filters.approval !== 'all') {
        params.approval = filters.approval;
      }
      const response = await userService.getAll(params);
      setUsers(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
      if (response.summary) {
        setSummary(response.summary);
      }
      
      // Fetch tenant assignments for each user (use tenantCount from response to skip if 0)
      const tenantData = {};
      for (const user of response.data || []) {
        if (user.tenantCount > 0) {
          try {
            const tenantResponse = await userService.getTenants(user.username);
            tenantData[user.username] = tenantResponse.data || [];
          } catch (err) {
            tenantData[user.username] = [];
          }
        } else {
          tenantData[user.username] = [];
        }
      }
      setUserTenants(tenantData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters.search, filters.status, filters.migrationStatus, filters.approval, sorting.sortBy, sorting.sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    setFilters({ ...filters, search: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleStatusFilter = (e) => {
    setFilters({ ...filters, status: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleTenantFilter = (e) => {
    setFilters({ ...filters, tenantId: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleMigrationFilter = (e) => {
    setFilters({ ...filters, migrationStatus: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleBulkMigrate = async () => {
    if (!window.confirm('This will provision personal workspaces for all legacy users without one. Continue?')) return;
    setBulkMigrateLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await userService.bulkMigrate();
      setMessage(response.message || `${response.migrated} users migrated`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Bulk migration failed');
    } finally {
      setBulkMigrateLoading(false);
    }
  };

  const handleMigrateUser = async (user) => {
    setActionLoading(user.username);
    setMessage(null);
    setError(null);
    try {
      const response = await userService.migrate(user.id);
      setMessage(response.message || 'Personal workspace created');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Migration failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return '↕';
    return sorting.sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleAction = async (action, user) => {
    setActionLoading(user.username);
    setMessage(null);
    setError(null);

    try {
      let response;
      switch (action) {
        case 'disable':
          response = await userService.disable(user.username);
          break;
        case 'enable':
          response = await userService.enable(user.username);
          break;
        case 'delete':
          response = await userService.delete(user.username);
          setDeleteConfirmUser(null);
          break;
        // Approval takes the UUID, not the email: the decision is proxied to
        // the login service, which addresses users by id only.
        case 'approve':
          response = await userService.approve(user.id);
          break;
        default:
          return;
      }
      setMessage(response.message);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Rejection is separated from handleAction because it carries a note, and
   * that note is emailed verbatim to the person being turned away. It gets a
   * confirm step for the same reason — this is the one admin action whose
   * output a stranger reads.
   */
  const handleReject = async (user, note) => {
    setActionLoading(user.username);
    setMessage(null);
    setError(null);
    try {
      const response = await userService.reject(user.id, note);
      setMessage(response.message || `Access rejected for ${user.email}`);
      setRejectUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateProfile = async (username, data) => {
    setActionLoading(username);
    try {
      const response = await userService.update(username, data);
      setMessage(response.message);
      setEditModalUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      ACTIVE: { class: 'status-active', label: 'Active' },
      PENDING: { class: 'status-pending', label: 'Pending' },
      DISABLED: { class: 'status-disabled', label: 'Disabled' },
    };
    const badge = badges[status] || { class: '', label: status };
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  // Invite-only approval (V248). Rendered as its OWN column, never merged into
  // the Status badge: "awaiting approval" and "disabled" are different facts and
  // an admin acts on them differently. Grandfathered users (approved before the
  // gate existed) have no decision record, and that absence is shown as such
  // rather than invented.
  const getApprovalBadge = (user) => {
    const s = user.approvalStatus;
    if (!s) return <span className="status-badge">—</span>;
    const map = {
      pending: { class: 'status-pending', label: '⏳ Pending' },
      approved: { class: 'status-active', label: '✓ Approved' },
      rejected: { class: 'status-disabled', label: '✕ Rejected' },
    };
    const b = map[s] || { class: '', label: s };
    const title = s === 'pending'
      ? `Requested ${user.approvalRequestedAt ? formatDate(user.approvalRequestedAt) : 'unknown'} — cannot sign in`
      // Prefer the human-readable name (V276); the email stays as back-compat
      // fallback for rows decided before the name was recorded.
      : (user.approvalDecidedByName || user.approvalDecidedBy)
        ? `${s} by ${user.approvalDecidedByName || user.approvalDecidedBy}${user.approvalNote ? ` — ${user.approvalNote}` : ''}`
        : 'Granted before invite-only existed (no decision on record)';
    return <span className={`status-badge ${b.class}`} title={title}>{b.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Filter users by tenant if selected
  const filteredUsers = filters.tenantId === 'all' 
    ? users 
    : users.filter(user => {
        const assignments = userTenants[user.username] || [];
        return assignments.some(a => a.tenant_id === filters.tenantId);
      });

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Users</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {summary.pending > 0 && (
            <button 
              className="btn btn-secondary" 
              onClick={handleBulkMigrate}
              disabled={bulkMigrateLoading}
              title={`${summary.pending} users need personal workspace provisioning`}
            >
              {bulkMigrateLoading ? 'Migrating...' : `Migrate ${summary.pending} Legacy Users`}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add User
          </button>
        </div>
      </div>

      {/* Migration Status Summary */}
      {(summary.migrated > 0 || summary.pending > 0) && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div style={{ padding: '0.5rem 1rem', background: '#f5f5f5', borderRadius: '8px', fontSize: '0.9rem' }}>
            <strong>{summary.total}</strong> total users
          </div>
          <div style={{ padding: '0.5rem 1rem', background: '#e6f7e6', borderRadius: '8px', fontSize: '0.9rem' }}>
            <strong>{summary.migrated}</strong> with personal workspace
          </div>
          {summary.pending > 0 && (
            <div style={{ padding: '0.5rem 1rem', background: '#fff3e0', borderRadius: '8px', fontSize: '0.9rem' }}>
              <strong>{summary.pending}</strong> legacy (no workspace)
            </div>
          )}
        </div>
      )}

      {message && (
        <div className="alert alert-success">{message}</div>
      )}
      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search users by email..."
            value={filters.search}
            onChange={handleSearch}
          />
        </div>
        <select value={filters.status} onChange={handleStatusFilter}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="disabled">Disabled</option>
        </select>
        <select value={filters.tenantId} onChange={handleTenantFilter}>
          <option value="all">All Tenants</option>
          {tenants.map(tenant => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}{tenant.isPersonal ? ' (Personal)' : ''}
            </option>
          ))}
        </select>
        <select value={filters.migrationStatus} onChange={handleMigrationFilter}>
          <option value="all">All Migration</option>
          <option value="migrated">Migrated</option>
          <option value="pending">Legacy (Pending)</option>
        </select>
        <select
          value={filters.approval}
          onChange={(e) => {
            setFilters(prev => ({ ...prev, approval: e.target.value }));
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          title="Invite-only approval state"
        >
          <option value="all">All Approval</option>
          <option value="pending">⏳ Awaiting approval</option>
          <option value="approved">✓ Approved</option>
          <option value="rejected">✕ Rejected</option>
        </select>
      </div>

      {/* The queue is the source of truth, not the notification email — if SES
          drops the "someone is waiting" mail, this banner is the only thing that
          surfaces a stalled request. Shown whenever anyone is waiting, even when
          the admin is filtered elsewhere. */}
      {filters.approval !== 'pending' && users.some(u => u.approvalStatus === 'pending') && (
        <div className="alert alert-warning">
          Some users are awaiting approval and cannot sign in.{' '}
          <button
            className="link-button"
            onClick={() => {
              setFilters(prev => ({ ...prev, approval: 'pending' }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            Review the queue →
          </button>
        </div>
      )}

      {/* User Table */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('name')}>
                    User {getSortIcon('name')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('status')}>
                    Status {getSortIcon('status')}
                  </th>
                  <th>Approval</th>
                  <th>Identity</th>
                  <th>Tenants</th>
                  <th>Products</th>
                  <th>Last Login</th>
                  <th className="sortable" onClick={() => handleSort('createdAt')}>
                    Created {getSortIcon('createdAt')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="empty-state">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.username}>
                      <td className="user-cell">
                        <div className="user-avatar" aria-label={`Avatar for ${user.name || user.email}`}>{user.name?.charAt(0) || user.email?.charAt(0) || '?'}</div>
                        <div className="user-info">
                          <div className="user-name">
                            <Link to={`/users/${encodeURIComponent(user.username || user.phone || user.id)}`}>{user.name || 'No name'}</Link>
                            {/* Which door this account came in through (V274) —
                                evidence of origin, from the signup audit event. */}
                            {user.signupChannel && (
                              <span title={`Signed up via ${user.signupChannel === 'phone' ? 'mobile OTP' : 'email OTP'}`}
                                style={{ marginLeft: 6, fontSize: '0.65rem', padding: '1px 5px', borderRadius: 4,
                                  background: user.signupChannel === 'phone' ? '#e3f2fd' : '#f3e5f5',
                                  color: user.signupChannel === 'phone' ? '#1565c0' : '#6a1b9a', verticalAlign: 'middle' }}>
                                {user.signupChannel === 'phone' ? 'Mobile' : 'Email'}
                              </span>
                            )}
                          </div>
                          {/* Contact channels with their proof state: ✓ = an OTP
                              to this exact value round-tripped (hover for when),
                              ○ = on file but never proven. */}
                          {user.email && (
                            <div className="user-email">
                              {user.email}{' '}
                              {user.emailVerifiedAt
                                ? <span title={`Email verified ${formatDate(user.emailVerifiedAt)}`} style={{ color: '#228b22' }}>✓</span>
                                : <span title="Email on file but never verified" style={{ color: '#bbb' }}>○</span>}
                            </div>
                          )}
                          {user.phone && (
                            <div className="user-email">
                              {user.phone}{' '}
                              {user.phoneVerifiedAt
                                ? <span title={`Mobile verified ${formatDate(user.phoneVerifiedAt)}`} style={{ color: '#228b22' }}>✓</span>
                                : <span title="Mobile on file but never verified" style={{ color: '#bbb' }}>○</span>}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{getStatusBadge(user.status)}</td>
                      <td>{getApprovalBadge(user)}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {user.migrationStatus === 'migrated' ? (
                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: '#e6f7e6', color: '#228b22', display: 'inline-block', width: 'fit-content' }}>
                              Workspace
                            </span>
                          ) : (
                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: '#fff3e0', color: '#e65100', display: 'inline-block', width: 'fit-content' }}>
                              Legacy
                            </span>
                          )}
                          {user.isSuperAdmin && (
                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: '#e3f2fd', color: '#1565c0', display: 'inline-block', width: 'fit-content' }}>
                              Super Admin
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <TenantBadge
                          count={(userTenants[user.username] || []).length || user.tenantCount || 0}
                          tenants={userTenants[user.username] || []}
                        />
                      </td>
                      {/* Which products this user can actually reach (V276):
                          derived server-side from grants × tenant enrollment,
                          so a campaigns grant on an unenrolled tenant earns no
                          chip. Its own column — distinct facts, distinct
                          columns (see the V248 note on Approval above). */}
                      <td>
                        {(user.productChips || []).length ? (
                          user.productChips.map(p => <ProductChip key={p} product={p} compact />)
                        ) : (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </td>
                      <td>{user.lastLogin ? formatDate(user.lastLogin) : <span style={{ color: '#999' }}>Never</span>}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        <div className="actions-dropdown">
                          <button className="btn btn-secondary btn-sm">
                            {actionLoading === user.username ? '...' : '⋮'}
                          </button>
                          <div className="dropdown-menu">
                            {/* Approval sits FIRST for anyone waiting: on a
                                queue screen it is the only action that matters,
                                and burying it under Edit Profile is how a
                                request sits unanswered for a week. */}
                            {user.approvalStatus === 'pending' && (
                              <>
                                <button
                                  className="dropdown-item text-success"
                                  onClick={() => handleAction('approve', user)}
                                >
                                  ✓ Approve access
                                </button>
                                <button
                                  className="dropdown-item text-danger"
                                  onClick={() => setRejectUser(user)}
                                >
                                  ✕ Reject access
                                </button>
                                <div className="dropdown-divider" />
                              </>
                            )}
                            {user.approvalStatus === 'rejected' && (
                              <button
                                className="dropdown-item text-success"
                                onClick={() => handleAction('approve', user)}
                              >
                                ✓ Approve access (reverse rejection)
                              </button>
                            )}
                            <Link to={`/users/${encodeURIComponent(user.username)}`} className="dropdown-item">
                              View Details
                            </Link>
                            <button className="dropdown-item" onClick={() => setEditModalUser(user)}>
                              Edit Profile
                            </button>
                            <button className="dropdown-item" onClick={() => setTenantAssignUser(user)}>
                              Assign to Tenant
                            </button>
                            {user.migrationStatus === 'pending' && (
                              <button className="dropdown-item text-success" onClick={() => handleMigrateUser(user)}>
                                Create Personal Workspace
                              </button>
                            )}
                            {user.status !== 'DISABLED' ? (
                              <button className="dropdown-item text-warning" onClick={() => handleAction('disable', user)}>
                                Disable Account
                              </button>
                            ) : (
                              <button className="dropdown-item text-success" onClick={() => handleAction('enable', user)}>
                                Enable Account
                              </button>
                            )}
                            <button className="dropdown-item text-danger" onClick={() => setDeleteConfirmUser(user)}>
                              Delete User
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalCount > 0 && pagination.totalPages > 1 && (
            <div className="pagination">
              <span>Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} users</span>
              <div className="pagination-buttons">
                <button 
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  ← Previous
                </button>
                <button 
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal 
          onClose={() => setShowAddModal(false)} 
          onSuccess={(msg) => { setMessage(msg); setShowAddModal(false); fetchUsers(); }}
          onError={setError}
        />
      )}

      {/* Edit Profile Modal */}
      {editModalUser && (
        <EditProfileModal
          user={editModalUser}
          onClose={() => setEditModalUser(null)}
          onSubmit={(data) => handleUpdateProfile(editModalUser.username, data)}
          loading={actionLoading === editModalUser.username}
        />
      )}

      {/* Delete Confirmation Modal */}
      {rejectUser && (
        <RejectAccessModal
          user={rejectUser}
          loading={actionLoading === rejectUser.username}
          onClose={() => setRejectUser(null)}
          onConfirm={(note) => handleReject(rejectUser, note)}
        />
      )}

      {deleteConfirmUser && (
        <DeleteConfirmModal
          user={deleteConfirmUser}
          onClose={() => setDeleteConfirmUser(null)}
          onConfirm={() => handleAction('delete', deleteConfirmUser)}
          loading={actionLoading === deleteConfirmUser.username}
        />
      )}

      {/* Tenant Assignment Modal */}
      {tenantAssignUser && (
        <TenantAssignmentModal
          user={tenantAssignUser}
          onClose={() => setTenantAssignUser(null)}
          onSuccess={(msg) => { setMessage(msg); setTenantAssignUser(null); fetchUsers(); }}
          onError={setError}
        />
      )}
    </div>
  );
};

// Add User Modal Component — OTP-based auth (no passwords needed)
const LEVELS = ['admin', 'editor', 'viewer'];

const AddUserModal = ({ onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [assignTenant, setAssignTenant] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [permissions, setPermissions] = useState({ campaigns: 'viewer', devices: 'viewer', account: 'viewer' });
  const [tenantSearch, setTenantSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Load tenants when step 2 opens
  useEffect(() => {
    if (!assignTenant) return;
    const fetch = async () => {
      setLoadingTenants(true);
      try {
        const res = await tenantService.getAll({ search: tenantSearch, type: 'organization' });
        setTenants(res.data || []);
      } catch (err) { console.error('Error loading tenants:', err); }
      finally { setLoadingTenants(false); }
    };
    fetch();
  }, [assignTenant, tenantSearch]);

  // Enrollment-aware editor: the tenant list rows carry products (V276), so
  // the selected row resolves which module columns exist. Computed in the
  // component body so render and submit agree even after the user switches
  // tenants. Before a tenant is picked this fails closed to account-only —
  // submit is disabled without a selection anyway.
  const selectedTenantRow = tenants.find(t => t.id === selectedTenant);
  const mods = enrolledModulesFromProducts(selectedTenantRow?.products);

  const setAllPermissions = (level) => {
    setPermissions(prev => {
      const next = { ...prev };
      mods.forEach(m => { next[m] = level; });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Step 1: Create user
      const response = await userService.create({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
      });

      // Step 2: Assign tenant if selected
      if (assignTenant && selectedTenant && response.data?.id) {
        try {
          // Only enrolled modules go over the wire — the endpoint upserts
          // exactly what is posted, so omitted modules stay untouched.
          const payload = Object.fromEntries(
            Object.entries(permissions).filter(([m]) => mods.includes(m))
          );
          await userService.assignTenant(response.data.id, selectedTenant, payload);
        } catch (err) {
          // User created but tenant assignment failed — still a partial success
          onSuccess(`${response.message} (Tenant assignment failed: ${err.response?.data?.error || err.message})`);
          return;
        }
      }

      onSuccess(response.message || 'User created successfully');
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const permLevelColors = { admin: '#dc3545', editor: '#fd7e14', viewer: '#6c757d' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', borderRadius: '12px' }}>
        <div className="modal-header" style={{ borderBottom: '1px solid #e4e6eb', padding: '20px 24px 16px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1c1e21' }}>Add new user</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#65676b' }}>Create an account that authenticates via OTP</p>
          </div>
          <button className="modal-close" onClick={onClose} style={{ background: '#e4e6eb', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.1rem', color: '#4b4f56' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: '16px' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'block' }}>Full name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="Jane Doe"
                  required
                  style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '10px 12px', fontSize: '0.95rem', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: '16px' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'block' }}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                  placeholder="+91 98765 43210"
                  style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '10px 12px', fontSize: '0.95rem', width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'block' }}>Email address</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
                placeholder="jane@company.com"
                required
                style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '10px 12px', fontSize: '0.95rem', width: '100%', boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: '0.8rem', color: '#65676b', marginTop: '4px', display: 'block' }}>Authentication is handled via OTP sent to this address</span>
            </div>

            {/* Tenant assignment toggle */}
            <div style={{ borderTop: '1px solid #e4e6eb', marginTop: '8px', paddingTop: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                <div style={{ position: 'relative', width: '36px', height: '20px' }}>
                  <input
                    type="checkbox"
                    checked={assignTenant}
                    onChange={e => setAssignTenant(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                  />
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: assignTenant ? '#1877f2' : '#ccd0d5', borderRadius: '10px',
                    transition: 'background 0.2s', cursor: 'pointer',
                  }} onClick={() => setAssignTenant(!assignTenant)}>
                    <div style={{
                      position: 'absolute', top: '2px', left: assignTenant ? '18px' : '2px',
                      width: '16px', height: '16px', background: '#fff', borderRadius: '50%',
                      transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1c1e21' }}>Assign to organization</span>
                  <span style={{ display: 'block', fontSize: '0.8rem', color: '#65676b' }}>A personal workspace is provisioned automatically on first login</span>
                </div>
              </label>
            </div>

            {assignTenant && (
              <div style={{ marginTop: '16px', padding: '16px', background: '#f0f2f5', borderRadius: '10px' }}>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', display: 'block' }}>Organization</label>
                  <input
                    type="text"
                    value={tenantSearch}
                    onChange={e => setTenantSearch(e.target.value)}
                    placeholder="Search tenants..."
                    style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '8px 12px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}
                  />
                  {loadingTenants ? (
                    <div style={{ fontSize: '0.85rem', color: '#65676b', padding: '8px 0' }}>Loading tenants...</div>
                  ) : (
                    <select
                      value={selectedTenant}
                      onChange={e => setSelectedTenant(e.target.value)}
                      required={assignTenant}
                      style={{ borderRadius: '8px', border: '1px solid #dddfe2', padding: '8px 12px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' }}
                    >
                      <option value="">Select a tenant</option>
                      {tenants.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>Permissions</label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {LEVELS.map(level => (
                        <button key={level} type="button"
                          onClick={() => setAllPermissions(level)}
                          style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: '6px', border: '1px solid #dddfe2', background: '#fff', cursor: 'pointer', textTransform: 'capitalize', color: '#444', fontWeight: 500 }}>
                          All {level}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {mods.map(mod => (
                      <div key={mod} style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '4px', color: '#444' }}>{MODULE_LABELS[mod] || mod}</div>
                        <select
                          value={permissions[mod]}
                          onChange={e => setPermissions(prev => ({ ...prev, [mod]: e.target.value }))}
                          style={{ width: '100%', borderRadius: '6px', border: '1px solid #dddfe2', padding: '6px 8px', fontSize: '0.85rem', color: permLevelColors[permissions[mod]] || '#444', fontWeight: 500 }}
                        >
                          {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={{ background: '#f0f2f5', borderRadius: '8px', padding: '12px 14px', marginTop: '16px', fontSize: '0.8rem', color: '#65676b', lineHeight: 1.5 }}>
              A personal workspace is created on first login. The user authenticates via OTP at <strong style={{ color: '#1c1e21' }}>login.galigali.in</strong> — no password required.
              {assignTenant && selectedTenant && <span> They will also be added to the selected organization.</span>}
            </div>
          </div>
          <div className="modal-footer" style={{ borderTop: '1px solid #e4e6eb', padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #dddfe2', background: '#e4e6eb', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500, color: '#444' }}>Cancel</button>
            <button type="submit" disabled={loading || (assignTenant && !selectedTenant)} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: loading ? '#a0c4ff' : '#1877f2', color: '#fff', cursor: loading ? 'default' : 'pointer', fontSize: '0.9rem', fontWeight: 600, opacity: (assignTenant && !selectedTenant) ? 0.5 : 1 }}>
              {loading ? 'Creating...' : assignTenant ? 'Create & Assign' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Users;
